import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Plumber, SearchFilters } from '../types';
import toast from 'react-hot-toast';
import { trackSearch } from '../utils/analytics';

export function usePlumberSearch(zipCode: string, filters: SearchFilters = {}) {
  const [plumbers, setPlumbers] = useState<Plumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Reset state when ZIP code changes
  useEffect(() => {
    setError(null);
    setPlumbers([]);
    setTotalCount(0);
    setCurrentPage(1);
  }, [zipCode]);

  // Reset state when ZIP code changes
  useEffect(() => {
    setError(null);
    setPlumbers([]);
    setTotalCount(0);
    setCurrentPage(1);
  }, [zipCode]);

  useEffect(() => {
    async function searchPlumbers() {
      try {
        if (!zipCode) return;
        
        setLoading(true);
        setError(null);

        // Get plumbers that serve this ZIP code
        const { data: plumbersData, error: plumbersError } = await supabase
          .from('plumbers')
          .select(`
            *,
            service_areas:plumber_service_areas(
              zip_code,
              city:cities!inner(
                name,
                state:state_id(code)
              )
            ),
            services:plumber_services(service_id),
            reviews(*)
          `);

        if (plumbersError) throw plumbersError;

        // Filter plumbers that serve this ZIP code or all states
        let filteredPlumbers = (plumbersData || []).filter(plumber => {
          // Check if plumber serves this specific ZIP code
          const servesZipCode = plumber.service_areas?.some(area => 
            area.zip_code === zipCode
          );

          // Check if plumber has no service areas (serves all areas)
          const servesAllAreas = !plumber.service_areas?.length;

          return servesZipCode || servesAllAreas;
        });

        // Apply service filters if specified
        if (filters.services?.length) {
          filteredPlumbers = filteredPlumbers.filter(plumber => {
            const plumberServices = plumber.services as any[];
            return filters.services!.some(serviceId =>
              plumberServices.some(service => 
                service.service_id === serviceId
              )
            );
          });
        }

        if (filters.rating) {
          filteredPlumbers = filteredPlumbers.filter(plumber =>
            plumber.rating >= filters.rating!
          );
        }

        if (filters.verified !== undefined) {
          filteredPlumbers = filteredPlumbers.filter(plumber =>
            plumber.is_verified === filters.verified
          );
        }

        if (filters.featured !== undefined) {
          filteredPlumbers = filteredPlumbers.filter(plumber =>
            plumber.is_featured === filters.featured
          );
        }

        // Sort plumbers
        if (filters.sortBy === 'rating') {
          filteredPlumbers.sort((a, b) => b.rating - a.rating);
        } else if (filters.sortBy === 'experience') {
          filteredPlumbers.sort((a, b) => (b.years_experience || 0) - (a.years_experience || 0));
        } else if (filters.sortBy === 'featured') {
          filteredPlumbers.sort((a, b) => {
            if (a.is_featured !== b.is_featured) {
              return a.is_featured ? -1 : 1;
            }
            return b.rating - a.rating;
          });
        } else {
          // Default sorting: featured first, then by rating
          filteredPlumbers.sort((a, b) => {
            if (a.is_featured !== b.is_featured) {
              return a.is_featured ? -1 : 1;
            }
            return b.rating - a.rating;
          });
        }

        // Apply pagination
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage - 1;
        
        // Set total count first
        setTotalCount(filteredPlumbers.length);
        
        if (filteredPlumbers.length > 0) {
          setPlumbers(filteredPlumbers.slice(start, start + itemsPerPage) as Plumber[]);
        } else {
          setPlumbers([] as Plumber[]);
        }
        
        // Track search analytics
        await trackSearch(zipCode, filteredPlumbers.length, filters);
      } catch (err) {
        console.error('Error searching plumbers:', err instanceof Error ? err.message : err);
        const message = err instanceof Error ? err.message : 'Failed to load plumbers';
        setError(message);
        toast.error(message);
        setPlumbers([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    }

    if (zipCode) {
      searchPlumbers();
    }
  }, [zipCode, filters, currentPage, itemsPerPage]);

  return {
    plumbers,
    loading,
    error,
    totalCount,
    currentPage,
    setCurrentPage,
    itemsPerPage
  };
}